import { useRef, useState, useCallback } from 'react'
import { Code2, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type HtmlBlockPreviewProps = {
  html: string
}

const MIN_HEIGHT = 80
const MAX_HEIGHT = 900

/**
 * Inline HTML preview for ```html fenced blocks inside markdown documents.
 * Renders a sandboxed iframe (Preview) with a Source toggle, auto-sized to content.
 * Distinct from HtmlDocumentViewer which is for whole HTML documents.
 */
export function HtmlBlockPreview({ html }: HtmlBlockPreviewProps) {
  const [mode, setMode] = useState<'preview' | 'source'>('preview')
  const [iframeHeight, setIframeHeight] = useState(MIN_HEIGHT)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const handleLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument
    if (!doc) return
    const h = doc.documentElement.scrollHeight || doc.body?.scrollHeight || MIN_HEIGHT
    setIframeHeight(Math.max(MIN_HEIGHT, Math.min(h + 2, MAX_HEIGHT)))
  }, [])

  return (
    <div className="not-prose my-4 overflow-hidden rounded-md border border-border bg-background">
      <div className="flex items-center gap-1 border-b border-border bg-muted/40 px-2 py-1.5">
        <span className="mr-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wide select-none">HTML</span>
        <Button
          type="button"
          variant={mode === 'preview' ? 'secondary' : 'ghost'}
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => setMode('preview')}
        >
          <Eye className="mr-1 h-3 w-3" aria-hidden />
          Preview
        </Button>
        <Button
          type="button"
          variant={mode === 'source' ? 'secondary' : 'ghost'}
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => setMode('source')}
        >
          <Code2 className="mr-1 h-3 w-3" aria-hidden />
          Source
        </Button>
      </div>

      {mode === 'preview' ? (
        <iframe
          ref={iframeRef}
          title="HTML preview"
          sandbox="allow-same-origin"
          srcDoc={html}
          onLoad={handleLoad}
          className={cn('w-full border-0 bg-white block')}
          style={{ height: iframeHeight }}
        />
      ) : (
        <pre className="overflow-x-auto whitespace-pre-wrap break-words bg-muted/20 p-4 font-mono text-xs text-foreground m-0 rounded-none">
          {html}
        </pre>
      )}
    </div>
  )
}
